/**
 * Onboarding Guard - ProtectedRoute Wrapper
 *
 * Checks if user has completed onboarding.
 * If not, redirects to /onboarding instead of requested page.
 * If complete, allows access to protected dashboard routes.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectIsAuthenticated,
  selectAuthLoading,
  selectTenantId,
} from '../../app/slices/authSlice';
import { useGetOnboardingStatusQuery } from '../../entities/auth/api/authApi';
import Icon from '../../components/AppIcon';
import PropTypes from 'prop-types';

/**
 * OnboardingGuard
 *
 * Wraps child components to enforce onboarding completion.
 *
 * Usage:
 * <Route element={<OnboardingGuard><Dashboard /></OnboardingGuard>} path="/dashboard" />
 */
export function OnboardingGuard({ children }) {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAuthLoading = useSelector(selectAuthLoading);
  const tenantId = useSelector(selectTenantId);
  const [loading, setLoading] = useState(true);

  // Fetch onboarding status
  const { data: status, isLoading: statusLoading } = useGetOnboardingStatusQuery(
    { tenant_id: tenantId },
    {
      skip: !isAuthenticated || !tenantId,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    // Keep guard loading until auth bootstrap finishes and onboarding query settles.
    setLoading(isAuthLoading || statusLoading);
  }, [isAuthLoading, statusLoading]);

  // Handle unauthenticated access
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);
  // Redirect to onboarding if not complete
  useEffect(() => {
    if (!loading && status && !status.has_completed_onboarding) {
      navigate('/onboarding');
    }
  }, [loading, status, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin mb-4 flex justify-center">
            <Icon name="Loader" size={40} className="text-blue-500" />
          </div>
          <p className="theme-text-secondary">Loading your account...</p>
        </div>
      </div>
    );
  }

  // Don't render if onboarding not complete
  if (status && !status.has_completed_onboarding) {
    return null;
  }

  return children;
}

OnboardingGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * useOnboardingStatus Hook
 *
 * Get onboarding completion status for a tenant.
 *
 * Usage:
 * const { hasCompleted, isLoading } = useOnboardingStatus(tenantId);
 */
export function useOnboardingStatus(tenantId) {
  const { data: status, isLoading } = useGetOnboardingStatusQuery(
    { tenant_id: tenantId },
    {
      skip: !tenantId,
    }
  );

  return {
    hasCompleted: status?.has_completed_onboarding ?? false,
    hasDemoData: status?.is_demo_data_set ?? false,
    firstUserAdded: status?.first_user_added ?? false,
    firstIntegrationConfigured: status?.first_integration_configured ?? false,
    isLoading,
  };
}
